import axios, { AxiosResponse } from "axios";
import { useAtom, useSetAtom } from "jotai";
import { useRef } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login/login";
import {
    GetInvoicesBody,
    IInvoice,
    InvoicesFilter,
    invoicesAtom,
    invoicesLoadingAtom,
    singleInvoiceAtom,
    invoicesAccumulatedAtom,
    invoicesHasMoreAtom,
    invoicesCurrentPageAtom,
    invoicesLoadingMoreAtom,
    invoicesCountAtom,
    invoicesDownloadingStatementAtom,
} from "./types";
import { PaginatedResponseType } from "../../globals/types";
import { isSuperAccess } from "../../utils/permissions";

type GetInvoices = (body: GetInvoicesBody) => Promise<PaginatedResponseType<IInvoice> | null>;
type GetInvoice = (invoiceId: number) => Promise<IInvoice | null>;
type RefundInvoice = (invoiceId: number) => Promise<IInvoice | null>;
type GetInvoicesAppend = (filters: InvoicesFilter, isLoadMore: boolean, pageSize?: number) => Promise<void>;
type DownloadStatement = (filters: InvoicesFilter) => Promise<void>;

type InvoiceKeyFunctions = {
    get: GetInvoices,
    getSingle: GetInvoice,
    refund: RefundInvoice,
    getInvoicesAppend: GetInvoicesAppend,
    downloadStatement: DownloadStatement,
}

type UseInvoices = [
    PaginatedResponseType<IInvoice>,
    IInvoice | null,
    InvoiceKeyFunctions
]

// #region normalizeDate
// Date inputs / quick-selects store YYYY-MM-DD. Expand to inclusive UTC bounds.
const normalizeStartDate = (date?: string): string | null => {
    if (!date) return null;
    return date.includes('T') ? date : `${date}T00:00:00.000Z`;
};
const normalizeEndDate = (date?: string): string | null => {
    if (!date) return null;
    return date.includes('T') ? date : `${date}T23:59:59.999Z`;
};

// #region buildFilterBody
const buildFilterBody = (filters: InvoicesFilter, brandId: number | null, pageSize: number, page: number): GetInvoicesBody => ({
    brand_id: brandId,
    page_size: pageSize,
    offset_pages: page,
    querystring: filters.query_string?.length ? filters.query_string : null,
    start_date: normalizeStartDate(filters.start_date),
    end_date: normalizeEndDate(filters.end_date),
    charge_statuses: filters.charge_statuses?.length ? filters.charge_statuses : null,
});

export const useInvoices = (): UseInvoices => {
    const [invoice, setInvoice] = useAtom(singleInvoiceAtom);
    const [invoices, setInvoices] = useAtom(invoicesAtom);
    const setInvoicesLoading = useSetAtom(invoicesLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [user, authToken, loginFunctions] = useLogin();

    // Infinite scroll state
    const [, setAccumulatedInvoices] = useAtom(invoicesAccumulatedAtom);
    const [, setHasMore] = useAtom(invoicesHasMoreAtom);
    const [currentPage, setCurrentPage] = useAtom(invoicesCurrentPageAtom);
    const [, setLoadingMore] = useAtom(invoicesLoadingMoreAtom);
    const [, setInvoicesCount] = useAtom(invoicesCountAtom);
    const setDownloadingStatement = useSetAtom(invoicesDownloadingStatementAtom);
    const latestRequestIdRef = useRef(0);

    // #region resolveBrandId
    // Non-super users are always locked to their own brand. Super users may switch:
    // filter brand_id undefined = own brand (default), null = all brands, number = that brand.
    const resolveBrandId = (filters: InvoicesFilter): number | null => {
        if (isSuperAccess(user) && filters.brand_id !== undefined) {
            return filters.brand_id;
        }
        return (user?.brand_id as number) ?? null;
    };

    const getInvoices = async (body: GetInvoicesBody): Promise<PaginatedResponseType<IInvoice> | null> => {
        setInvoicesLoading(true);
        const getInvoices: AxiosResponse<PaginatedResponseType<IInvoice>> | void = await axios.post(`${environment.invoicesUrl}`, body, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (getInvoices) {
            setInvoices(getInvoices.data);
            setInvoicesLoading(false);
            return getInvoices.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error retrieving invoices. Please try again later.'
            });
            setInvoicesLoading(false);
            return null;
        }
    }

    // #region getInvoicesAppend
    const getInvoicesAppend: GetInvoicesAppend = async (filters, isLoadMore, pageSize = 20) => {
        const requestId = ++latestRequestIdRef.current;
        const brandId = resolveBrandId(filters);

        if (!isLoadMore) {
            // Reset: fresh fetch
            setAccumulatedInvoices([]);
            setCurrentPage(0);
            setHasMore(true);
            setInvoicesLoading(true);

            const body = buildFilterBody(filters, brandId, pageSize, 0);
            const response: AxiosResponse<PaginatedResponseType<IInvoice>> | void = await axios.post(
                `${environment.invoicesUrl}`,
                body,
                { headers: { 'X-Wallmates-Auth': authToken } }
            ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

            if (requestId !== latestRequestIdRef.current) return; // stale

            if (response) {
                setAccumulatedInvoices(response.data.results);
                setInvoices(response.data);
                setInvoicesCount(response.data.count);
                setHasMore(response.data.hasNext);
                setCurrentPage(0);
            }
            setInvoicesLoading(false);
        } else {
            // Load more: append next page
            const nextPage = currentPage + 1;
            setLoadingMore(true);

            const body = buildFilterBody(filters, brandId, pageSize, nextPage);
            const response: AxiosResponse<PaginatedResponseType<IInvoice>> | void = await axios.post(
                `${environment.invoicesUrl}`,
                body,
                { headers: { 'X-Wallmates-Auth': authToken } }
            ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

            if (requestId !== latestRequestIdRef.current) return; // stale

            if (response) {
                setAccumulatedInvoices((prev) => [...prev, ...response.data.results]);
                setHasMore(response.data.hasNext);
                setCurrentPage(nextPage);
                setInvoicesCount(response.data.count);
            }
            setLoadingMore(false);
        }
    }

    // #region downloadStatement
    const downloadStatement: DownloadStatement = async (filters) => {
        setDownloadingStatement(true);
        const brandId = resolveBrandId(filters);
        const body = {
            brand_id: brandId,
            querystring: filters.query_string?.length ? filters.query_string : null,
            start_date: normalizeStartDate(filters.start_date),
            end_date: normalizeEndDate(filters.end_date),
            charge_statuses: filters.charge_statuses?.length ? filters.charge_statuses : null,
        };

        const response: AxiosResponse<Blob> | void = await axios.post(
            `${environment.invoicesUrl}/statement`,
            body,
            {
                responseType: 'blob',
                headers: { 'X-Wallmates-Auth': authToken }
            }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (response) {
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `invoice-statement-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(url);
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error generating the statement. Please try again later.'
            });
        }
        setDownloadingStatement(false);
    }

    const getInvoice = async (invoiceId: number): Promise<IInvoice | null> => {
        setInvoicesLoading(true);
        const getInvoice: AxiosResponse<IInvoice> | void = await axios.get(`${environment.invoicesUrl}/${invoiceId}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (getInvoice) {
            setInvoice(getInvoice.data);
            setInvoicesLoading(false);
            return getInvoice.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error retrieving the invoice. Please try again later.'
            });
            setInvoicesLoading(false);
            return null;
        }
    }

    const refundInvoice = async (invoiceId: number): Promise<IInvoice | null> => {
        const response: AxiosResponse<IInvoice> | void = await axios.post(`${environment.invoicesUrl}/${invoiceId}/refund`, {}, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (response) {
            return response.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error refunding the invoice. Please try again later.'
            });
            return null;
        }
    }

    const keyFunctions: InvoiceKeyFunctions = {
        get: getInvoices,
        getSingle: getInvoice,
        refund: refundInvoice,
        getInvoicesAppend: getInvoicesAppend,
        downloadStatement: downloadStatement,
    }

    return [
        invoices,
        invoice,
        keyFunctions
    ]
}
